/**
 * Ten per page for every list in the person panel (Jose, 2026-09-22: long
 * lists in the panel were a scroll wall).
 *
 * The pager is the web app's shared `TablePagination` — the same control the
 * Imports and My Searches tables use — so the panel pages the way the rest of
 * the app does. It is shown only when a list is longer than one page: a
 * "Showing 3 events" caption under every short card of a drawer is noise.
 *
 * `resetKey` puts the list back on page 1 whenever what it lists changes (a
 * filter chip, another person). The page is also clamped to the last page, so
 * a list that shrinks under a reader on page 3 lands on its new last page
 * rather than on an empty one.
 */
import { useState } from "react";

import { TablePagination } from "../../components/TablePagination";

export const PANEL_PAGE_SIZE = 10;

export interface Paged<T> {
  /** The rows of the current page. */
  items: T[];
  page: number;
  total: number;
  setPage: (page: number) => void;
}

export function usePagedList<T>(all: readonly T[], resetKey: string): Paged<T> {
  const [state, setState] = useState<{ key: string; page: number }>({ key: resetKey, page: 1 });
  const pageCount = Math.max(1, Math.ceil(all.length / PANEL_PAGE_SIZE));
  const requested = state.key === resetKey ? state.page : 1;
  const page = Math.min(Math.max(1, requested), pageCount);
  const start = (page - 1) * PANEL_PAGE_SIZE;
  return {
    items: all.slice(start, start + PANEL_PAGE_SIZE),
    page,
    total: all.length,
    setPage: (next) => setState({ key: resetKey, page: next }),
  };
}

/** The shared pager under a panel list, or nothing when the list fits. */
export function PanelPager<T>({
  paged,
  noun,
  nounPlural,
  testId,
}: {
  paged: Paged<T>;
  noun: string;
  nounPlural?: string;
  testId: string;
}) {
  if (paged.total <= PANEL_PAGE_SIZE) return null;
  return (
    <div className="mt-3 border-t border-border">
      <TablePagination
        total={paged.total}
        page={paged.page}
        pageSize={PANEL_PAGE_SIZE}
        onPageChange={paged.setPage}
        noun={noun}
        nounPlural={nounPlural}
        testId={testId}
      />
    </div>
  );
}
